import ConfirmModal from "@/components/core/modals/ConfirmModal";
import {
  requestGetAlertPeriodicity,
  requestUpdateAlertPeriodicity,
} from "@/state/alertPeriodicity/AlertPeriodictyEffects";
import { RecapPeriodicityDTO } from "@/state/alertPeriodicity/dto/response/recap.periodicity.dto";
import { ALERT_PERIODICITY_DAYS } from "@/utils/Constant";
import { toastError } from "@/utils/helpers/toast-helper";
import { Checkbox, Col, Row } from "antd";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface AlertPeriodicityProps {
  setAlertPeriodicityIsNever: (alertPeriodicityIsNever: boolean) => void;
}

const AlertPeriodicity: FunctionComponent<AlertPeriodicityProps> = ({
  setAlertPeriodicityIsNever,
}) => {
  const { t } = useTranslation();

  const [
    isAlertRecapPeriodicityConfirmationModalOpen,
    setIsAlertRecapPeriodicityConfirmationModalOpen,
  ] = useState<boolean>(false);
  const [everyday, setEveryday] = useState<boolean>(false);
  const [never, setNever] = useState<boolean>(false);
  const [alertPeriodicity, setAlertPeriodicity] =
    useState<RecapPeriodicityDTO>();

  /* API calls */
  useEffect(() => {
    void requestGetAlertPeriodicity({});
  }, []);

  /* hook to grab never change to send it to parent componebt */
  useEffect(() => {
    setAlertPeriodicityIsNever(never);
  }, [never]);

  useEffect(() => {
    return requestGetAlertPeriodicity.done.watch(({ result }) => {
      if (result.ok && result.data) {
        const recapPeriodicity: RecapPeriodicityDTO = result.data;
        const periodicityValues: boolean[] = Object.values(recapPeriodicity);

        const allTrue = periodicityValues.every((value) => value);
        const allFalse = periodicityValues.every((value) => !value);

        if (allTrue) {
          setEveryday(true);
          setNever(false);
        } else if (allFalse) {
          setEveryday(false);
          setNever(true);
        } else {
          setEveryday(false);
          setNever(false);
        }
        setAlertPeriodicity(recapPeriodicity);
      } else {
        toastError(t("error.user.getAlertRecapPeriodicity"));
      }
    });
  }, [t]);

  const updateAllDaysToValue = (value: boolean) => {
    setAlertPeriodicity({
      sendOnMonday: value,
      sendOnTuesday: value,
      sendOnWednesday: value,
      sendOnThursday: value,
      sendOnFriday: value,
      sendOnSaturday: value,
      sendOnSunday: value,
    });
  };

  const updateSpecificDay = (
    day: keyof RecapPeriodicityDTO,
    value: boolean,
  ) => {
    const newAlertPeriodicity = {
      ...alertPeriodicity,
      [day]: value,
    } as RecapPeriodicityDTO;
    if (newAlertPeriodicity) {
      void requestUpdateAlertPeriodicity({
        dto: {
          recapPeriodicity: newAlertPeriodicity,
          never: false,
          everyday: false,
        },
      });
      setAlertPeriodicity(newAlertPeriodicity);
      setNever(false);
      setEveryday(false);
    }
  };

  const updateEveryday = () => {
    if (everyday) {
      // No need to do API call and actions if we are already set to everyday === true
      return;
    }

    if (alertPeriodicity) {
      void requestUpdateAlertPeriodicity({
        dto: {
          everyday: true,
          never: false,
          recapPeriodicity: alertPeriodicity,
        },
      });
    }
    updateAllDaysToValue(true);
    setEveryday(true);
    setNever(false);
  };

  const updateNever = () => {
    if (never) {
      // No need to do API call and actions if we are already set to never === true
      return;
    }
    if (alertPeriodicity) {
      void requestUpdateAlertPeriodicity({
        dto: {
          everyday: false,
          never: true,
          recapPeriodicity: alertPeriodicity,
        },
      });
    }
    updateAllDaysToValue(false);
    setEveryday(false);
    setNever(true);
  };

  const alertPeriodicityCell = (
    key: string,
    className: string,
    label: string,
    checked: boolean,
    setChecked: (value: boolean) => void,
  ) => {
    return (
      <div
        key={`MyPreferencesScreen_ALERT_PERIODICITY_DAYS_${key}`}
        className={className}
      >
        <Checkbox
          id={`${key}_periodicity`}
          checked={checked}
          onChange={(event) => setChecked(event.target.checked)}
        >
          {label}
        </Checkbox>
      </div>
    );
  };

  return (
    <Row gutter={16}>
      <Col span={24} sm={24} md={4} xl={6} />
      <Col span={24} sm={12} md={8} xl={6}>
        {t("account.myPreferences.recap.periodicity")}
      </Col>
      <Col span={24} sm={12}>
        {alertPeriodicityCell(
          "everyday",
          "",
          "Tous les jours",
          everyday,
          updateEveryday,
        )}
        {Object.keys(ALERT_PERIODICITY_DAYS).map((_, index) => {
          const key = Object.keys(ALERT_PERIODICITY_DAYS)[index];
          const className = ALERT_PERIODICITY_DAYS[key].className;
          const label = ALERT_PERIODICITY_DAYS[key].label;

          return alertPeriodicityCell(
            key,
            className,
            label,
            alertPeriodicity
              ? alertPeriodicity[key as keyof RecapPeriodicityDTO]
              : false,
            (value) =>
              updateSpecificDay(key as keyof RecapPeriodicityDTO, value),
          );
        })}
        {alertPeriodicityCell("never", "", "Jamais", never, () => {
          if (!never) {
            setIsAlertRecapPeriodicityConfirmationModalOpen(true);
          }
        })}
      </Col>
      <ConfirmModal
        visible={isAlertRecapPeriodicityConfirmationModalOpen}
        onCancel={() => setIsAlertRecapPeriodicityConfirmationModalOpen(false)}
        onOk={() => {
          setIsAlertRecapPeriodicityConfirmationModalOpen(false);
          updateNever();
        }}
        title={t("alertPreferences.recapPeriodicity.modalTitle")}
        content={t("alertPreferences.recapPeriodicity.modalDescription")}
        okText={t("alertPreferences.recapPeriodicity.yesAction")}
        cancelText={t("alertPreferences.recapPeriodicity.noAction")}
      />
    </Row>
  );
};

export default AlertPeriodicity;
